/**
 * Module dependencies.
 */

import { ReactElement, useMemo } from 'react';
import { Subtitle, Type } from 'src/components/core';
import { color, units } from '@untile/react-components';
import { theme } from 'styled-tools';
import { useAdvisor } from 'src/context/advisor/context';
import SidebarStep from './sidebar-step';
import map from 'lodash/map';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  border-right: 1px solid ${color.transparentize('grey800', 0.08)};
  display: flex;
  flex-direction: column;
  margin-top: ${units(6)};
  min-height: calc(100vh - ${theme('dimensions.navbarHeight')}px);
  overflow: auto;
  padding: 0 25%;
  position: relative;
  width: 100%;
`;

/**
 * `ContentWrapper` styled component.
 */

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: content;
`;

/**
 * `StepsWrapper` styled component.
 */

const StepsWrapper = styled.div`
  display: grid;
  grid-auto-columns: 100%;
  grid-row-gap: ${units(3)};
`;

/**
 * `Sidebar` component.
 */

const Sidebar = ({ className }: Props): ReactElement => {
  const { translate } = useTranslate();
  const { choices, onGoToStep } = useAdvisor();
  const totalChoices = useMemo(() => choices.length, [choices]);

  return (
    <Wrapper className={className}>
      <ContentWrapper>
        <Type.H2 marginBottom={units(1)}>{translate('advisor:title')}</Type.H2>

        <Subtitle marginBottom={units(5)}>
          {totalChoices > 1
            ? translate('advisor:yourChoices')
            : translate('advisor:lead')}
        </Subtitle>

        <StepsWrapper>
          {map(choices, ({ pageKey, value }, index) => (
            <SidebarStep
              key={index}
              onClick={() => onGoToStep(index)}
              pageKey={pageKey}
              value={String(value)}
            />
          ))}
        </StepsWrapper>
      </ContentWrapper>
    </Wrapper>
  );
};

/**
 * Export `Sidebar` component.
 */

export default Sidebar;
